import clsx from 'clsx'
import { CSSProperties, FC, PointerEvent, useCallback, useEffect, useRef, useState } from 'react'
import type { CommentHubAuthor } from 'shared-code/commenthub-api'
import CommentIcon from 'shared-svg/CommentHollow.svg'
import ChevronIcon from 'shared-svg/ArrowRightBird.svg'
import { AppEventsContext } from '../../contexts/AppEventsContext'
import { AuthContext } from '../../contexts/AuthContext'
import { PageBodyContext } from '../../contexts/PageBodyContext'
import { PostContext } from '../../contexts/PostContext'
import PinMark from '../PinMark'
import { ScrollUpContext } from '../../contexts/ScrollUpContext'
import { useWindowScrollState } from '../../hooks/use-window-scroll-state'
import { ASK_LINE, SAY_WHY_ACTION, SIGN_IN_TO_SAY_WHY } from '../../polls/ask-copy'
import { usePoll } from '../../polls/PollStoreContext'
import { pollTotal, type PollState } from '../../polls/poll-store'
import { avatarColor, commentSnippet, commentsLabel } from '../comment-format'
import CommentHubAvatar from '../CommentHubAvatar'
import { CONVERSATION_DOCK_ATTR, ConversationContext } from './ConversationContext'
import {
  formatNewShort,
  formatNewSince,
  newSinceLastRead,
  readConversationLastRead,
} from './conversationLastRead'
import { trimLabel, votesLabel } from './ConversationPoll'
import { SIGN_IN_TO_SAY_WHY as SIGN_IN_TO_SAY_WHY_DIALOG } from './have-your-say'
import styles from './ConversationStickyBar.module.css'
import { ConversationPreview, useConversationPreview } from './useConversationPreview'

interface ConversationStickyBarProps {
  /** The host page has another fixed surface (SG LabCompare) — the bar stays away. */
  hidden?: boolean
}

/** The bar shows once the reader is about a viewport in — the title and byline count are gone. */
export const BAR_REVEAL_VIEWPORTS = 0.8

/** After the sheet closes, scroll samples for this long are skipped (the lock's restore). */
const CLOSE_SETTLE_MS = 300

/** A sideways drag past this share of the bar's width dismisses it on release. */
export const SWIPE_DISMISS_SHARE = 0.35

/** Below this many px of movement a touch is still a tap; past it, the larger axis decides. */
const SWIPE_SLOP_PX = 8

/** How long the bar takes to slide off after a swipe (the CSS `.leaving` transition). */
const SWIPE_OUT_MS = 180

/** The asked face's "Your pick" chip: the label is cut at this many characters. */
const BAR_PICK_TRIM = 22

/** The poll face's title while the total is unknown or zero — never "0 votes". */
export const POLL_FACE_TITLE = "Readers' poll"

export type TopBarMode = 'poll' | 'asked' | 'active' | 'empty'

/**
 * Which face the bar shows, pure (Guillaume's mobile design, 2026-10-25):
 * - `poll`: the article has a conversation poll the reader has not voted in — the active face's
 *   layout with poll words (faces of those who voted and said why, "N votes", the question,
 *   Vote ›); the whole face opens the panel, whose poll takes the vote;
 * - `asked` (2026-10-28): the reader just voted in the ARTICLE body (`barAsked`) and has not
 *   posted — "Your pick" and "Why this one?" with Say why ›. The bar itself never votes (its
 *   poll face opens the panel), so only an article vote gets here. It needs no preview, so it
 *   wins over the loading check too;
 * - `empty`: the thread has no comment yet — "Start the conversation";
 * - `active`: anything else, including a preview that failed (no count, "Join").
 * Null until something is known: the preview still loading and no poll to show.
 */
export function topBarMode(params: {
  pollUnvoted: boolean
  /** `barAsked(pollState)` — only read once the poll is voted. */
  asked: boolean
  previewStatus: ConversationPreview['status']
  total: number | null
}): TopBarMode | null {
  if (params.pollUnvoted) {
    return 'poll'
  }
  if (params.asked) {
    return 'asked'
  }
  if (params.previewStatus === 'idle' || params.previewStatus === 'loading') {
    return null
  }
  return params.previewStatus === 'ready' && params.total === 0 ? 'empty' : 'active'
}

/**
 * Whether the bar asks "Why this one?": a vote counted on this page from the article body
 * (`lastVote.from === 'article'`) the reader has not yet posted with. A vote read back on load
 * leaves `lastVote` null (no ask); a bar vote opens the sheet, whose nudge asks instead.
 */
export function barAsked(state: PollState | null): boolean {
  return (
    state !== null &&
    state.myChoiceId !== null &&
    state.lastVote?.from === 'article' &&
    !state.posted
  )
}

/**
 * Where the bar's top sits: under the site header while it is slid back in (scrolled up), else
 * under the under-nav strip (`--rail-toc-stack` minus its 16px gap, published by UnderNavBlock)
 * or the 4px reading-progress line. CSS does the max (`ConversationStickyBar.module.css`).
 */
function barStyle(headerShown: boolean, headerHeight: number, progress: boolean): CSSProperties {
  return {
    '--conv-bar-header': headerShown ? `${headerHeight}px` : '0px',
    // The progress line rides under the slid-back header: flush with the header then.
    '--conv-bar-min-top': progress && !headerShown ? '4px' : '0px',
  } as CSSProperties
}

export function conversationBarDismissedKey(containerId: string): string {
  return `conversation-bar:dismissed:${containerId}`
}

function readDismissed(containerId: string): boolean {
  try {
    return window.sessionStorage.getItem(conversationBarDismissedKey(containerId)) === '1'
  } catch {
    return false // storage blocked: the bar shows
  }
}

/** A swipe — for the session: nothing brings the bar back until the next visit. */
function writeDismissed(containerId: string): void {
  try {
    window.sessionStorage.setItem(conversationBarDismissedKey(containerId), '1')
  } catch {
    // storage blocked: the choice holds for this page only
  }
}

/** Where the bar's top goes while the site header is shown: the dock row's height, else the
 *  header's, kept current. */
function useSiteHeaderHeight(): number {
  const [height, setHeight] = useState(0)
  useEffect(() => {
    const header = document.querySelector('header')
    if (!header) {
      return undefined
    }
    const dock = header.querySelector<HTMLElement>(`[${CONVERSATION_DOCK_ATTR}]`) ?? header
    const measure = (): void => setHeight(dock.offsetHeight)
    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(dock)
    return () => observer?.disconnect()
  }, [])
  return height
}

/** Read › / Comment › / Say why ›: the bar's one action, a text link in the brand accent.
 *  `pulse`: the asked face's ring, played until its animation ends (`onPulseEnd`). */
const BarAction: FC<{
  label: string
  onClick: () => void
  pulse?: boolean
  onPulseEnd?: () => void
}> = ({ label, onClick, pulse = false, onPulseEnd }) => (
  <button
    type="button"
    className={clsx(styles.action, pulse && styles.actionPulse)}
    onClick={onClick}
    onAnimationEnd={e => {
      if (e.target === e.currentTarget) {
        onPulseEnd?.()
      }
    }}
  >
    {label}
    <ChevronIcon className={styles.actionIcon} aria-hidden="true" />
  </button>
)

/** The overlapping faces of the lead (the active face's recent commenters, the poll face's
 *  readers who voted and said why); nothing when there are none. */
const Faces: FC<{ authors: readonly CommentHubAuthor[] }> = ({ authors }) => {
  if (!authors.length) {
    return null
  }
  return (
    <span className={styles.faces} aria-hidden="true">
      {authors.map(author => (
        <CommentHubAvatar
          key={author.id}
          className={styles.face}
          src={author.avatarUrl}
          name={author.name ?? 'Anonymous'}
          color={avatarColor(author.id)}
        />
      ))}
    </span>
  )
}

interface SwipeState {
  id: number
  x: number
  y: number
  /** Null until the slop is passed; then whether the drag is sideways (ours) or a scroll. */
  sideways: boolean | null
}

/**
 * Swipe left or right to dismiss (Guillaume, 2026-10-25). Only a sideways drag is taken — a
 * vertical one stays the page's scroll (`touch-action: pan-y`) — and a drag never ends as a tap
 * on the button it started on. Below `SWIPE_DISMISS_SHARE` of the width it springs back.
 */
function useSwipeToDismiss(onDismiss: () => void): {
  offset: number
  leaving: boolean
  handlers: {
    onPointerDown: (e: PointerEvent<HTMLElement>) => void
    onPointerMove: (e: PointerEvent<HTMLElement>) => void
    onPointerUp: (e: PointerEvent<HTMLElement>) => void
    onPointerCancel: () => void
    onClickCapture: (e: React.MouseEvent) => void
  }
} {
  const [offset, setOffset] = useState(0)
  const [leaving, setLeaving] = useState(false)
  const swipe = useRef<SwipeState | null>(null)
  const dragged = useRef(false)

  const reset = (): void => {
    swipe.current = null
    setOffset(0)
  }

  return {
    offset,
    leaving,
    handlers: {
      onPointerDown: e => {
        if (e.pointerType === 'mouse' || leaving) {
          return
        }
        dragged.current = false
        swipe.current = { id: e.pointerId, x: e.clientX, y: e.clientY, sideways: null }
      },
      onPointerMove: e => {
        const s = swipe.current
        if (!s || s.id !== e.pointerId) {
          return
        }
        const dx = e.clientX - s.x
        const dy = e.clientY - s.y
        if (s.sideways === null) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_SLOP_PX) {
            return
          }
          s.sideways = Math.abs(dx) > Math.abs(dy)
          if (s.sideways) {
            dragged.current = true
            e.currentTarget.setPointerCapture?.(e.pointerId)
          }
        }
        if (s.sideways) {
          setOffset(dx)
        }
      },
      onPointerUp: e => {
        const s = swipe.current
        if (!s || s.id !== e.pointerId) {
          return
        }
        const width = e.currentTarget.offsetWidth || 1
        const dx = e.clientX - s.x
        if (s.sideways && Math.abs(dx) >= width * SWIPE_DISMISS_SHARE) {
          swipe.current = null
          setLeaving(true)
          setOffset(Math.sign(dx) * width * 1.2)
          window.setTimeout(onDismiss, SWIPE_OUT_MS)
          return
        }
        reset()
      },
      onPointerCancel: reset,
      onClickCapture: e => {
        if (dragged.current) {
          dragged.current = false
          e.preventDefault()
          e.stopPropagation()
        }
      },
    },
  }
}

/**
 * The mobile (below `lg`) conversation bar, docked at the TOP of the viewport (Guillaume's design,
 * 2026-10-25 — the bottom belongs to the anchor ad and the outstream video). Four faces
 * (`topBarMode`):
 * - active thread: up to three recent commenters' faces, "N comments · 3 new" (new since the
 *   last read, `conversationLastRead.ts`; nothing on a first visit), the highlighted comment's
 *   first line ("Name: …"), and Read ›;
 * - no comment yet: "No comments yet / Be the first to weigh in" and Comment › (composer
 *   focused);
 * - the article's conversation poll, unvoted (2026-10-29, the editors' phone feedback): the
 *   active face's shape with poll words — the faces of the last readers who voted AND said why
 *   (`recentPickAuthors`), "N votes" (`POLL_FACE_TITLE` until a total above zero is known), the
 *   question on one line, and Vote › — the whole face opens the panel, whose poll takes the vote.
 *   The answers never sit in the bar: the sideways pill row (2026-10-25) cost a second row of
 *   chrome over the article and fought the swipe-to-dismiss and the browser's edge swipe.
 * - voted in the article body, not yet posted (`barAsked`, 2026-10-28): the "Your pick" chip,
 *   "Why this one?" and Say why › (composer focused; signed out, Sign in to say why › opens the
 *   sign-in) — one line at 375px, the chip's label giving way first.
 * The look is direction A of the 2026-10-25 canvas (a quiet white strip in the article's own
 * type, like the byline), the same on both brands in each brand's accent.
 * It shows once the reader is `BAR_REVEAL_VIEWPORTS` in; swiped left or right it leaves for the
 * session (a visually hidden "Hide" button does the same for keyboard and screen-reader users).
 * It sits under the site header while that is slid back in (on SoundGuys phones in place of the
 * header's search row, `CONVERSATION_DOCK_ATTR`), else under the TOC strip; the
 * under-nav deal bar and best-buy panel step aside while it shows (`barShown`, UnderNavBlock).
 * Client-only by construction: nothing renders until a mount-time scroll check.
 */
const ConversationStickyBar: FC<ConversationStickyBarProps> = ({ hidden = false }) => {
  const conversation = ConversationContext.useOptionalContainer()
  const appEvents = AppEventsContext.useOptionalContainer()
  const auth = AuthContext.useOptionalContainer()
  const post = PostContext.useOptionalContainer()
  const scrollUp = ScrollUpContext.useOptionalContainer()
  const pageBody = PageBodyContext.useOptionalContainer()
  const containerId = conversation?.containerId ?? null
  const preview = useConversationPreview(containerId)
  const poll = (containerId && post?.conversationPoll) || null
  const pollState = usePoll(poll)
  const headerHeight = useSiteHeaderHeight()
  const [revealed, setRevealed] = useState(false)
  // null until read after mount — SSR and hydration both render nothing.
  const [dismissed, setDismissed] = useState<boolean | null>(null)
  const [lastRead, setLastRead] = useState<number | null>(null)
  /** The `lastVote.seq` whose asked-face pop and pulse have played: once per vote, not per remount. */
  const [playedAskSeq, setPlayedAskSeq] = useState(0)

  // The sheet's scroll lock can zero `scrollY`; while it is open the reveal state is frozen so
  // the opener never unmounts under it.
  const openRef = useRef(false)
  openRef.current = conversation?.isOpen ?? false
  /** Just closed: the lock's scroll restore is a jump, not a reader's scroll — skip it. */
  const settleUntil = useRef(0)

  const check = useCallback(() => {
    if (openRef.current || Date.now() < settleUntil.current) {
      return
    }
    setRevealed(window.scrollY > window.innerHeight * BAR_REVEAL_VIEWPORTS)
  }, [])
  useWindowScrollState(check)

  useEffect(() => {
    // A reload mid-page or a deep link never fires a first scroll event. Not re-run on close:
    // the lock's scroll restore fires a scroll event, and checking earlier could read the
    // locked scrollY (0) and unmount the opener before focus returns to it.
    check()
  }, [check])

  const isOpen = conversation?.isOpen ?? false
  const wasOpen = useRef(false)
  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      settleUntil.current = Date.now() + CLOSE_SETTLE_MS
    }
    wasOpen.current = isOpen
    // The host marks the read on open and close; read it back when the sheet is closed.
    if (containerId && !isOpen) {
      setLastRead(readConversationLastRead(containerId))
    }
  }, [isOpen, containerId])

  useEffect(() => {
    setDismissed(containerId ? readDismissed(containerId) : null)
  }, [containerId])

  const dismiss = useCallback(() => {
    if (!containerId) {
      return
    }
    writeDismissed(containerId)
    setDismissed(true)
    appEvents?.appEvent({ event: 'conversation-bar-dismissed', containerId })
  }, [containerId, appEvents])
  const swipe = useSwipeToDismiss(dismiss)

  const total = preview.status === 'ready' ? preview.total : null
  const liveMode = topBarMode({
    pollUnvoted: pollState !== null && pollState.myChoiceId === null,
    asked: barAsked(pollState),
    previewStatus: preview.status,
    total,
  })
  // The face is latched while the sheet is open: the reader's first post (empty → active, asked
  // → active) or a vote must not swap the opener's subtree under the open sheet (Directive 373eb31cc).
  const [mode, setMode] = useState<TopBarMode | null>(null)
  useEffect(() => {
    if (!isOpen) {
      setMode(liveMode)
    }
  }, [liveMode, isOpen])
  const visible = Boolean(
    conversation && containerId && !hidden && dismissed === false && revealed && mode
  )

  const setBarShown = conversation?.setBarShown
  useEffect(() => {
    setBarShown?.(visible)
  }, [visible, setBarShown])
  useEffect(() => () => setBarShown?.(false), [setBarShown])

  if (!conversation || !visible || !mode) {
    return null
  }
  const { open } = conversation
  // While the sheet is open the bar stays MOUNTED, only moved out of sight: the sheet returns
  // focus to its opener on close (Directive 373eb31cc), so the opener must still exist and be
  // focusable — no unmount, no `visibility: hidden`, no `inert`.
  const away = conversation.isOpen ? styles.away : null
  const style = {
    ...barStyle(
      scrollUp?.scrolledUp ?? false,
      headerHeight,
      pageBody?.showReadingProgress ?? false
    ),
    ...(swipe.offset
      ? {
          transform: `translateX(${swipe.offset}px)`,
          opacity: Math.max(0.2, 1 - Math.abs(swipe.offset) / 320),
        }
      : null),
  } as CSSProperties

  const hide = (
    <button type="button" className={styles.hide} onClick={dismiss}>
      {'Hide comments bar'}
    </button>
  )

  let body: JSX.Element
  if (mode === 'poll' && poll && pollState) {
    const faces = preview.status === 'ready' ? preview.recentPickAuthors : []
    const votes = pollTotal(pollState)
    const openBar = (): void => open('bar')
    body = (
      <>
        <button type="button" className={styles.lead} onClick={openBar}>
          <Faces authors={faces} />
          <span className={styles.copy}>
            <span className={styles.title}>{votes > 0 ? votesLabel(votes) : POLL_FACE_TITLE}</span>
            <span className={styles.sub} title={pollState.question}>
              {pollState.question}
            </span>
          </span>
        </button>
        <BarAction label="Vote" onClick={openBar} />
      </>
    )
  } else if (mode === 'asked' && pollState) {
    const pick = pollState.choices.find(c => c.id === pollState.myChoiceId)
    const seq = pollState.lastVote?.seq ?? 0
    const fresh = seq > playedAskSeq
    const openFocused = (): void => open('bar', { focusComposer: true })
    // Signed out the panel never focuses its read-only composer (Constraint d01b4141e), so
    // opening it would not sign anyone in: the action opens the sign-in itself, as
    // PollFootnote's does. "Say why" until the stored session is probed — no label flash.
    const signedOut = auth !== null && auth.authResolved && !auth.authState?.user
    body = (
      <>
        <button type="button" className={styles.lead} onClick={openFocused}>
          {pick ? (
            <span className={clsx(styles.pick, fresh && styles.pickPop)} title={pick.label}>
              <svg
                className={styles.pickCheck}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                aria-hidden="true"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
              <span className={styles.pickLabel}>
                <span className={styles.srOnly}>{'Your pick: '}</span>
                {trimLabel(pick.label, BAR_PICK_TRIM)}
              </span>
            </span>
          ) : null}
          <span className={styles.askLine}>{ASK_LINE}</span>
        </button>
        <BarAction
          label={signedOut ? SIGN_IN_TO_SAY_WHY : SAY_WHY_ACTION}
          onClick={
            signedOut ? () => void auth.showLogIn({ copy: SIGN_IN_TO_SAY_WHY_DIALOG }) : openFocused
          }
          pulse={fresh}
          onPulseEnd={() => setPlayedAskSeq(seq)}
        />
      </>
    )
  } else if (mode === 'empty') {
    const openFocused = (): void => open('bar', { focusComposer: true })
    body = (
      <>
        <button type="button" className={styles.lead} onClick={openFocused}>
          <CommentIcon className={styles.emptyIcon} aria-hidden="true" />
          <span className={styles.copy}>
            <span className={styles.title}>{'No comments yet'}</span>
            <span className={styles.sub}>{'Be the first to weigh in'}</span>
          </span>
        </button>
        <BarAction label="Comment" onClick={openFocused} />
      </>
    )
  } else {
    const faces = preview.status === 'ready' ? preview.recentAuthors : []
    const fresh =
      preview.status === 'ready' ? newSinceLastRead(preview.recentCreatedAt, lastRead) : null
    // The thread's first line: the highlighted comment (featured, else top), else the newest.
    const quoted = preview.status === 'ready' ? preview.highlight?.comment ?? preview.newest : null
    const snippet = quoted ? commentSnippet(quoted.body) : ''
    const openBar = (): void => open('bar')
    body = (
      <>
        <button type="button" className={styles.lead} onClick={openBar}>
          <Faces authors={faces} />
          <span className={styles.copy}>
            <span className={styles.title}>
              {total ? commentsLabel(total) : 'The discussion'}
              {fresh ? (
                <span className={styles.fresh} title={formatNewSince(fresh)}>
                  {` · ${formatNewShort(fresh)}`}
                </span>
              ) : null}
            </span>
            {quoted && snippet ? (
              <span className={styles.sub}>
                {quoted.pinned || quoted.featured ? (
                  <>
                    <PinMark />{' '}
                  </>
                ) : null}
                {`${quoted.author.name ?? 'Anonymous'}: ${snippet}`}
              </span>
            ) : null}
          </span>
        </button>
        <BarAction label="Read" onClick={openBar} />
      </>
    )
  }

  return (
    <div
      className={clsx(styles.bar, away, {
        [styles.dragging]: swipe.offset !== 0 && !swipe.leaving,
        [styles.leaving]: swipe.leaving,
      })}
      style={style}
      role="region"
      aria-label="Conversation bar"
      {...swipe.handlers}
    >
      {hide}
      {body}
    </div>
  )
}

export default ConversationStickyBar
